import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { selectModules } from '../../../redux/moduleSlice'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import FormFieldBuilder from "../../../components/FormFieldBuilder";
import { formDataSanitizer } from '../../../helpers/formValidation'

export default function CustomerDetailsModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { register, handleSubmit, control, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const active = modal.is_active && modal.type === 'customer_details_modal'
  const inFlightCustomerId = useRef(null)
  const [customerInfo, setCustomerInfo] = useState(null)
  const [customerIdLoaded, setCustomerIdLoaded] = useState(null)
  const dropdownOptions = useSelector(selectDropdownOptions)

  const [module, setModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const navi = useNavigate()
  const modules = useSelector(selectModules)
  const dropDowns = useSelector(selectDropdowns)
  const [stateFields, setStateFields] = useState([]);

  const customerFields = ['email_address', 'first_name', 'last_name', 'address', 'address2', 'city', 'state_dropdown_option_id', 'zip_code_id', 'home_phone_number', 'cell_phone_number', 'birth_date', 'auth_3rd_party_contact_name', 'auth_3rd_party_date', 'tags']
  const customerFields2 = ['employer_name', 'employer_phone', 'employer_phone_ext', 'supervisor_name', 'supervisor_phone', 'supervisor_phone_ext', 'hire_date', 'job_title', 'industry_dropdown_option_id']
  const customerFields3 = ['income_type_dropdown_option_id', 'net_monthly_income']
  const customerFields4 = ['is_military', 'is_ssp_disabled', 'is_debit_card_disabled', 'is_consent_email_transactional']
  const customerFields5 = ['nonfundable_reason_dropdown_option_id', 'language_dropdown_option_id', 'contact_preference_time_dropdown_option_id']

  const handleStateFields = (name, value, module) => {
    const stateFieldName = `${module.code_key}_${name}`;
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => {
        if (stateFieldName === item?.name) {
          return {
            ...item,
            value: value
          }
        } else {
          return item
        }
      }))
    } else {
      setStateFields([
        ...stateFields,
        {
          name: stateFieldName,
          value: value
        }
      ])
    }
  }

  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === 'customer')

      if (m) {
        setModule(m)

        const mf = [...m.module_fields]
        mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
        setModuleFields(mf)
      }
    }
  }, [modules.data])

  useEffect(() => {
    return () => {
      setCustomerInfo(null);
    };
  }, []);

  // This modal stays mounted in the Footer and its fields take the loaded customer only as
  // defaultValue (applied once at registration). Clear the previous customer on every open and drop
  // late responses, so a previous customer's details can never become this customer's form values.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getCustomerInformation(customerId)
    } else {
      inFlightCustomerId.current = null
    }
  }, [active, customerId])
  const getCustomerInformation = async (customerId) => {
    inFlightCustomerId.current = customerId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}`)
    try {
      const response = await request.get()
      if (inFlightCustomerId.current !== customerId) return
      if (response.status === 200 || response.status === 201) {
        setCustomerInfo(response?.data?.data)
        setCustomerIdLoaded(response?.data?.data?.id)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightCustomerId.current !== customerId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const resetState = () => {
    setCustomerInfo(null);
    setCustomerIdLoaded(null);
    setStateFields([]);
    setSaving(false);
    reset();
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const onSubmit = async (formData) => {
    setSaving(true)
    let sanitizedData = formDataSanitizer(formData, module, dropdownOptions)
    //send back the customer id loaded when the modal is opened
    //then the backend can verify if the updating customer id match with what was loaded
    sanitizedData['customer_id_loaded'] = customerIdLoaded

    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/customer_details`)
    try {
      const response = await request.modify(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: '' }))
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } });
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };
  const formRender = () => {
    if (moduleFields && module) {
      const filterAndSortByCustomFields = (moduleFields, customerFields) => {
        return moduleFields
          .filter((e) => customerFields.includes(e.column_name))
          .sort((a, b) => {
            return customerFields.indexOf(a.column_name) - customerFields.indexOf(b.column_name);
          });
      };

      const filteredFields1 = filterAndSortByCustomFields(moduleFields, customerFields);
      const filteredFields2 = filterAndSortByCustomFields(moduleFields, customerFields2);
      const filteredFields3 = filterAndSortByCustomFields(moduleFields, customerFields3);
      const filteredFields4 = filterAndSortByCustomFields(moduleFields, customerFields4);
      const filteredFields5 = filterAndSortByCustomFields(moduleFields, customerFields5);

      const renderFields = (fields) => {
        // Until this opening's customer has loaded, render nothing rather than the previous one's fields.
        if (!customerInfo || String(customerIdLoaded) !== String(customerId)) {
          return null;
        }
        return fields.map((m, index) => {
          const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key;
          const selectOptions = {
            dropdownOptions: dropDowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
            is_editable: true,
            fieldType,
          };
          const fieldNameLookup = { zip_code_id: customerInfo.zip_code_label }

          const defaultValue = fieldNameLookup[m.column_name] || customerInfo[m.column_name];

          return (
            <FormFieldBuilder
              field={m}
              selectOptions={selectOptions}
              register={register}
              errors={errors}
              control={control}
              key={m.id}
              row={customerInfo}
              sourceModule={{
                id: customerInfo[m.column_name],
                name: defaultValue,
                column_name: m.column_name,
              }}
              handleStateFields={(name, value) => { handleStateFields(name, value, module) }}
              stateFields={stateFields}
              data={defaultValue}
            />
          );
        });
      };

      return (
        <>
          <div className="fields-container">
            {renderFields(filteredFields1)}
          </div>
          <hr />
          <div className="fields-container">
            {renderFields(filteredFields2)}
          </div>
          <hr />
          <div className="fields-container">
            {renderFields(filteredFields3)}
          </div>
          <hr />
          <div className="fields-container">
            {renderFields(filteredFields4)}
          </div>
          <hr />
          <div className="fields-container">
            {renderFields(filteredFields5)}
          </div>
        </>
      );
    } else {
      return <Loading />;
    }
  };

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="customer_details_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-customer-details column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Customer Details
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body"
            style={{ minHeight: "110px" }}
          >
            {formRender()}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Saving' : 'Save'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
